import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  SimpleGrid,
  Badge,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Avatar,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetEmployee, useRestoreEmployee, useGetEmployeeOnboardingDocuments, downloadEmployeeOnboardingDocument } from '../../services/employeeService';
import { useGetPlanInfo } from '../../services/planService';
import { useUserContext } from '../../context/UserContext';
import { formatCurrency } from '../../utils/currency';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiHome,
  FiClock,
  FiCalendar,
  FiArrowLeft,
  FiEdit,
  FiRefreshCw,
  FiDownload,
} from 'react-icons/fi';

/** Display API `employment_start_date` (YYYY-MM-DD) in local calendar form. */
const formatEmploymentStartDisplay = (ymd) => {
  if (!ymd) return null;
  const parts = String(ymd).slice(0, 10).split("-");
  if (parts.length !== 3) return ymd;
  const [y, m, d] = parts.map((p) => parseInt(p, 10));
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatOnboardingDocDate = (iso) => {
  if (!iso) return 'N/A';
  const normalized = /Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  return new Date(normalized).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ViewEmployee = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useUserContext();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Get plan info for country-based currency
  const { planInfo } = useGetPlanInfo();

  const {
    getEmployeeData,
    getEmployeeIsLoading,
    getEmployeeError,
    getEmployeeIsFetched,
  } = useGetEmployee({ employeeId, enabled: true });

  const {
    onboardingDocsData,
    onboardingDocsIsLoading,
    onboardingDocsError,
  } = useGetEmployeeOnboardingDocuments({
    employeeId,
    enabled: Boolean(employeeId && getEmployeeIsFetched && !getEmployeeError),
  });

  const onboardingDocuments = onboardingDocsData?.documents || [];
  const [downloadingDocId, setDownloadingDocId] = useState(null);

  const handleDownloadOnboardingDoc = async (doc) => {
    try {
      setDownloadingDocId(doc.id);
      await downloadEmployeeOnboardingDocument(employeeId, doc.id);
    } catch (e) {
      console.error(e);
      toast({
        title: "Download failed",
        description: "Could not download this document. Try again or check your connection.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Restore employee hook
  const { restoreEmployeePayload, restoreEmployeeIsLoading } = useRestoreEmployee(() => {
    // Refetch employee data after restore
    window.location.reload();
  });

  const employee = getEmployeeData;
  const isAdmin = user?.role === "company_admin";

  const handleEdit = () => {
    navigate(`/employee/add-employee?id=${employeeId}`);
  };

  const handleBack = () => {
    navigate('/employee');
  };

  const handleRestore = () => {
    if (window.confirm('Restore this employee? They will be able to clock in and perform operations again.')) {
      restoreEmployeePayload(employeeId);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (getEmployeeIsLoading) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  if (getEmployeeError) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <Alert status="error">
          <AlertIcon />
          Failed to load employee data. Please try again.
        </Alert>
      </Box>
    );
  }

  if (!employee || Object.keys(employee).length === 0) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <Alert status="info">
          <AlertIcon />
          Employee not found.
        </Alert>
      </Box>
    );
  }

  const getAttendanceStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present':
        return 'green';
      case 'absent':
        return 'red';
      case 'late':
        return 'orange';
      case 'on leave':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch" mb={8}>
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <Button
              leftIcon={<FiArrowLeft />}
              variant="ghost"
              onClick={handleBack}
              colorScheme="blue"
            >
              Back to Employees
            </Button>
            <Box>
              <Heading size="lg" color="gray.900" fontWeight="600">
                Employee Details
              </Heading>
              <Text color="gray.600" fontSize="md">
                Complete information for {employee.first_name} {employee.last_name}
              </Text>
            </Box>
          </HStack>
          <HStack spacing={2}>
            {employee.status === "archived" && isAdmin && (
              <Button
                leftIcon={<FiRefreshCw />}
                colorScheme="blue"
                onClick={handleRestore}
                isLoading={restoreEmployeeIsLoading}
              >
                Restore Employee
              </Button>
            )}
            <Button
              leftIcon={<FiEdit />}
              colorScheme="blue"
              onClick={handleEdit}
              isDisabled={employee.status === "archived"}
            >
              Edit Employee
            </Button>
          </HStack>
        </HStack>
      </VStack>

      {/* Archived Alert */}
      {employee.status === "archived" && (
        <Alert status="warning" mb={6} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="600">This employee has been archived</Text>
            <Text fontSize="sm" color="gray.700">
              Archived on: {formatDate(employee.archived_at)} by {employee.archived_by || 'System'}
            </Text>
            <Text fontSize="sm" color="red.700" mt={2}>
              ⚠️ Archived employees cannot clock in, request leave, or perform other operations.
            </Text>
          </Box>
        </Alert>
      )}

      {/* Employee Profile Card */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor} shadow="sm" mb={8}>
        <CardHeader>
          <HStack spacing={6} align="center">
            <Avatar
              size="xl"
              src={employee.profile_image}
              name={`${employee.first_name} ${employee.last_name}`}
              bg="blue.100"
              color="blue.600"
            />
            <VStack align="start" spacing={2}>
              <Heading size="md" color="gray.900">
                {employee.first_name} {employee.last_name}
              </Heading>
              <Text color="gray.600" fontSize="md">
                {employee.designation}
              </Text>
              <HStack spacing={4}>
                <Badge colorScheme="blue" variant="subtle">
                  {employee.employee_id}
                </Badge>
                <Badge
                  colorScheme={employee.status === "archived" ? "red" : "green"}
                  variant="subtle"
                >
                  {employee.status === "archived" ? "ARCHIVED" : "ACTIVE"}
                </Badge>
                <Badge
                  colorScheme={getAttendanceStatusColor(employee.attendance_status)}
                  variant="subtle"
                >
                  {employee.attendance_status}
                </Badge>
              </HStack>
            </VStack>
          </HStack>
        </CardHeader>
      </Card>

      {/* Employee Information Grid */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
        {/* Personal Information */}
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} shadow="sm">
          <CardHeader>
            <Heading size="md" color="gray.900">
              Personal Information
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <InfoItem
                label="Full Name"
                value={`${employee.first_name} ${employee.last_name}`}
                icon={<FiUser />}
              />
              <InfoItem
                label="Email"
                value={employee.email}
                icon={<FiMail />}
              />
              <InfoItem
                label="Phone Number"
                value={employee.phone_number}
                icon={<FiPhone />}
              />
              <InfoItem
                label="Employee ID"
                value={employee.employee_id}
                icon={<FiUser />}
              />
            </VStack>
          </CardBody>
        </Card>

        {/* Work Information */}
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} shadow="sm">
          <CardHeader>
            <Heading size="md" color="gray.900">
              Work Information
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <InfoItem
                label="Designation"
                value={employee.designation}
                icon={<FiUser />}
              />
              <InfoItem
                label="Employment start"
                value={
                  employee.employment_start_date
                    ? formatEmploymentStartDisplay(employee.employment_start_date)
                    : "Not set"
                }
                icon={<FiCalendar />}
              />
              <InfoItem
                label="Tenure (approx.)"
                value={
                  employee.tenure_years != null && employee.tenure_years !== ""
                    ? `${Number(employee.tenure_years).toFixed(2)} yrs`
                    : "—"
                }
                icon={<FiClock />}
              />
              <InfoItem
                label="Department"
                value={employee.department}
                icon={<FiHome />}
              />
              <InfoItem
                label="Location"
                value={employee.location}
                icon={<FiMapPin />}
              />
              <InfoItem
                label="Reporting Senior"
                value={employee.reporting_senior}
                icon={<FiUser />}
              />
              <InfoItem
                label="Shift"
                value={employee.shift}
                icon={<FiClock />}
              />
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Salary and Attendance Statistics */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
        {/* Salary Information */}
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} shadow="sm">
          <CardHeader>
            <Heading size="md" color="gray.900">
              Salary Information
            </Heading>
          </CardHeader>
          <CardBody>
            <Stat>
              <StatLabel color="gray.600">Annual Salary</StatLabel>
              <StatNumber color="green.600" fontSize="3xl">
                {employee.salary ? formatCurrency(employee.salary, planInfo?.country) : 'N/A'}
              </StatNumber>
              <StatHelpText>Per year</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Attendance Statistics */}
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} shadow="sm">
          <CardHeader>
            <Heading size="md" color="gray.900">
              Attendance Statistics
            </Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={2} spacing={4}>
              <Stat>
                <StatLabel color="gray.600">Hours Worked</StatLabel>
                <StatNumber color="blue.600">
                  {employee.total_hours_worked || 0}
                </StatNumber>
                <StatHelpText>Total hours</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel color="gray.600">Missed Hours</StatLabel>
                <StatNumber color="red.600">
                  {employee.missed_hours || 0}
                </StatNumber>
                <StatHelpText>Hours missed</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel color="gray.600">Attendance %</StatLabel>
                <StatNumber color="green.600">
                  {Math.round(employee.attendance_percentage || 0)}%
                </StatNumber>
                <StatHelpText>Success rate</StatHelpText>
                <Box mt={2}>
                  <Progress
                    value={employee.attendance_percentage || 0}
                    colorScheme="green"
                    size="sm"
                    borderRadius="md"
                    hasStripe
                    isAnimated
                  />
                </Box>
              </Stat>
              <Stat>
                <StatLabel color="gray.600">Status</StatLabel>
                <Badge
                  colorScheme={getAttendanceStatusColor(employee.attendance_status)}
                  variant="subtle"
                  fontSize="md"
                  p={2}
                >
                  {employee.attendance_status || 'Unknown'}
                </Badge>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Onboarding documents */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor} shadow="sm" mb={8}>
        <CardHeader>
          <Heading size="md" color="gray.900">
            Onboarding documents
          </Heading>
        </CardHeader>
        <CardBody>
          {onboardingDocsIsLoading && (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          )}
          {onboardingDocsError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Could not load onboarding documents.
            </Alert>
          )}
          {!onboardingDocsIsLoading && !onboardingDocsError && onboardingDocuments.length === 0 && (
            <Text color="gray.600" fontSize="sm">
              No onboarding documents uploaded for this employee.
            </Text>
          )}
          {!onboardingDocsIsLoading && !onboardingDocsError && onboardingDocuments.length > 0 && (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Title</Th>
                    <Th>Uploaded</Th>
                    <Th>By</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {onboardingDocuments.map((doc) => (
                    <Tr key={doc.id}>
                      <Td fontWeight="500">{doc.title}</Td>
                      <Td fontSize="sm">{formatOnboardingDocDate(doc.created_at)}</Td>
                      <Td fontSize="sm">
                        {(doc.uploaded_by_name && String(doc.uploaded_by_name).trim()) ||
                          doc.uploaded_by_email ||
                          "—"}
                      </Td>
                      <Td textAlign="right">
                        <Button
                          size="sm"
                          leftIcon={<FiDownload />}
                          variant="outline"
                          isLoading={downloadingDocId === doc.id}
                          onClick={() => handleDownloadOnboardingDoc(doc)}
                        >
                          Download
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Time Information */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor} shadow="sm">
        <CardHeader>
          <Heading size="md" color="gray.900">
            Time Information
          </Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack spacing={4} align="stretch">
              <Text fontWeight="600" color="gray.700">
                Average Clock-in Time
              </Text>
              <HStack spacing={3}>
                <FiClock color="#4A5568" />
                <Text fontSize="lg" color="gray.900">
                  {formatTime(employee.avg_clock_in_time)}
                </Text>
              </HStack>
            </VStack>
            <VStack spacing={4} align="stretch">
              <Text fontWeight="600" color="gray.700">
                Average Clock-out Time
              </Text>
              <HStack spacing={3}>
                <FiClock color="#4A5568" />
                <Text fontSize="lg" color="gray.900">
                  {formatTime(employee.avg_clock_out_time)}
                </Text>
              </HStack>
            </VStack>
          </SimpleGrid>
        </CardBody>
      </Card>
    </Box>
  );
};

const InfoItem = ({ label, value, icon }) => (
  <HStack spacing={3} align="center">
    <Box color="gray.500">{icon}</Box>
    <VStack align="start" spacing={0} flex={1}>
      <Text fontSize="sm" color="gray.600" fontWeight="500">
        {label}
      </Text>
      <Text fontSize="md" color="gray.900" fontWeight="600">
        {value || 'N/A'}
      </Text>
    </VStack>
  </HStack>
);

export default ViewEmployee; 